import React from 'react';
import { motion } from 'framer-motion';
import { HelpCircle, MousePointer, Link, Shield, Target, Activity, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const GuideModal = ({ isOpen, onClose, className = "" }) => {
  const { t } = useTranslation();

  if (!isOpen) return null;

  const steps = [
    {
      title: t('workshop2.activity3.guide.selectRiskSources'),
      description: t('workshop2.activity3.guide.selectRiskSourcesDesc'),
      icon: <MousePointer size={20} className="text-blue-500" />
    },
    {
      title: t('workshop2.activity3.guide.associateDreadedEvents'),
      description: t('workshop2.activity3.guide.associateDreadedEventsDesc'),
      icon: <Link size={20} className="text-purple-500" />
    },
    {
      title: t('workshop2.activity3.guide.understandSecurityPillars'),
      description: t('workshop2.activity3.guide.understandSecurityPillarsDesc'),
      icon: <Shield size={20} className="text-orange-500" />
    },
    {
      title: t('workshop2.activity3.guide.evaluateGravity'),
      description: t('workshop2.activity3.guide.evaluateGravityDesc'),
      icon: <Target size={20} className="text-green-500" />
    },
    {
      title: t('workshop2.activity3.guide.consultSummaryTable'),
      description: t('workshop2.activity3.guide.consultSummaryTableDesc'),
      icon: <Activity size={20} className="text-red-500" />
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
      className={`bg-white rounded-2xl shadow-xl border border-gray-200 mb-6 ${className}`}
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 p-6 rounded-t-2xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <HelpCircle size={24} className="text-white mr-3" />
            <h2 className="text-xl font-bold text-white">{t('workshop2.activity3.guide.title')}</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-full text-white/70 hover:text-white hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white transition-colors"
            aria-label={t('common.close', 'Close')}
          >
            <X size={24} />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <div className="space-y-8">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="flex"
            >
              <div className="flex-shrink-0 mr-4">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 text-blue-600 font-bold">
                  {index + 1}
                </div>
              </div>
              <div className="flex-1">
                <div className="flex items-center mb-2">
                  {step.icon}
                  <h3 className="text-lg font-semibold text-gray-800 ml-2">{step.title}</h3>
                </div>
                <p className="text-gray-600">{step.description}</p>
              </div>
            </motion.div>
          ))}

          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-md mt-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <Activity className="h-5 w-5 text-blue-500" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">{t('workshop2.activity3.guide.tip')}</h3>
                <div className="mt-2 text-sm text-blue-700">
                  <p>
                    {t('workshop2.activity3.guide.tipMessage')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default GuideModal;